/**
 * Connexion chiffrée TLS 1.2, pour les messageries qui n'acceptent pas encore
 * TLS 1.3 (SFR, La Poste).
 *
 * Même principe que tunnel.js : le chiffrement est négocié ici, de bout en
 * bout avec le serveur mail, et le relais ne voit passer que des octets
 * chiffrés. On s'appuie sur node-forge, complété pour TLS 1.2 (src/tls12).
 * Le certificat du serveur est vérifié contre les autorités racines de
 * Mozilla, et son nom doit correspondre au serveur demandé.
 */
import forge from 'node-forge/lib/forge.js';
import 'node-forge/lib/pki.js';
import './tls12/tls.cjs';
import './tls12/aesCipherSuites.cjs';

const versOctets = (chaine) => {
  const o = new Uint8Array(chaine.length);
  for (let i = 0; i < chaine.length; i++) o[i] = chaine.charCodeAt(i) & 0xff;
  return o;
};

const versChaine = (octets) => {
  let s = '';
  for (let i = 0; i < octets.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, octets.subarray(i, i + 0x8000));
  }
  return s;
};

/** Autorités racines lisibles par forge (il ignore les clés à courbe elliptique). */
export function magasinAutorites(pem) {
  const magasin = forge.pki.createCaStore();
  for (const bloc of pem.match(/-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/g) || []) {
    try { magasin.addCertificate(forge.pki.certificateFromPem(bloc)); } catch { /* clé non RSA : ignorée */ }
  }
  return magasin;
}

/** Le certificat couvre-t-il ce nom de serveur (y compris *.exemple.fr) ? */
function nomCorrespond(certificat, hote) {
  const ext = certificat.getExtension('subjectAltName');
  const noms = ext && ext.altNames ? ext.altNames.filter((a) => a.type === 2).map((a) => a.value) : [];
  if (!noms.length) {
    const cn = certificat.subject.getField('CN');
    if (cn) noms.push(cn.value);
  }
  const h = hote.toLowerCase();
  return noms.some((n) => {
    const nom = String(n).toLowerCase();
    if (nom.startsWith('*.')) return h.endsWith(nom.slice(1)) && h.split('.').length === nom.split('.').length;
    return nom === h;
  });
}

/**
 * Ouvre une connexion TLS 1.2 vers `hote` au-dessus d'un transport brut
 * { envoyer(octets), surDonnees(fn), surFermeture(fn), fermer() }.
 * Renvoie { lire, ecrire, fermer } comme tunnel.js.
 */
export function ouvrirTls12(hote, transport, autorites) {
  const recus = [];
  const attentes = [];
  let ferme = false;
  let erreur = null;

  const livrer = () => {
    while (attentes.length && (recus.length || ferme || erreur)) {
      const { ok, ko } = attentes.shift();
      if (recus.length) ok(recus.shift());
      else if (erreur) ko(erreur);
      else ok(undefined);
    }
  };

  return new Promise((ouvert, echec) => {
    const connexion = forge.tls.createConnection({
      server: false,
      caStore: autorites,
      sessionCache: {},
      virtualHost: hote,
      cipherSuites: [
        forge.tls.CipherSuites.TLS_RSA_WITH_AES_128_CBC_SHA,
        forge.tls.CipherSuites.TLS_RSA_WITH_AES_256_CBC_SHA
      ],
      verify(c, verifie, profondeur, certificats) {
        if (verifie !== true) return verifie;
        if (profondeur === 0 && !nomCorrespond(certificats[0], hote)) {
          return { alert: forge.tls.Alert.Description.bad_certificate, message: `Certificat émis pour un autre serveur que ${hote}.` };
        }
        return true;
      },
      connected() {
        ouvert({
          lire: () => new Promise((ok, ko) => { attentes.push({ ok, ko }); livrer(); }),
          ecrire: async (octets) => { connexion.prepare(versChaine(octets)); },
          fermer: () => { try { connexion.close(); } catch { /* déjà fermée */ } transport.fermer(); }
        });
      },
      tlsDataReady(c) {
        const donnees = c.tlsData.getBytes();
        if (donnees.length) transport.envoyer(versOctets(donnees));
      },
      dataReady(c) {
        const donnees = c.data.getBytes();
        if (donnees.length) { recus.push(versOctets(donnees)); livrer(); }
      },
      closed() {
        ferme = true;
        livrer();
      },
      error(c, e) {
        erreur = new Error(`Connexion chiffrée refusée : ${e.message || e}`);
        echec(erreur);
        livrer();
      }
    });
    transport.surDonnees((octets) => connexion.process(versChaine(octets)));
    transport.surFermeture(() => { ferme = true; livrer(); });
    connexion.handshake();
  });
}
